import { onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useDashboardToast } from '@/composables/useDashboardToast'
import { refreshDataAfterUpgrade } from '@/composables/refreshDataAfterUpgrade'

const PLAN_POLL_ATTEMPTS = 6
const PLAN_POLL_INTERVAL_MS = 2500

/**
 * Handles the return from a redirect-based Payment Element method (e.g. PayPal, 3DS
 * fallback). Stripe always sends the browser back to the return_url and appends
 * redirect_status=succeeded|failed|pending plus payment_intent params — so a bare
 * ?upgrade=success is only a success when redirect_status agrees (or is absent, which
 * never happens on real Stripe returns but keeps the param backwards-compatible).
 */
export function useUpgradeReturn() {
  const route = useRoute()
  const router = useRouter()
  const store = useStore()
  const { t } = useI18n()
  const { showToast } = useDashboardToast()

  onMounted(async () => {
    const status = route.query.upgrade
    if (status !== 'success' && status !== 'cancelled') return

    const redirectStatus = route.query.redirect_status

    const query = { ...route.query }
    delete query.upgrade
    delete query.redirect_status
    delete query.payment_intent
    delete query.payment_intent_client_secret
    router.replace({ path: route.path, query })

    if (status !== 'success') return

    if (redirectStatus === 'failed') {
      showToast(t('Dashboard.Toast.UpgradeFailed'))
      return
    }

    // succeeded, pending (e.g. some bank redirects), or absent: the webhook flips the
    // plan moments later, so poll briefly instead of reading it once.
    for (let attempt = 0; attempt < PLAN_POLL_ATTEMPTS; attempt++) {
      await store.dispatch('fetchDonorPlan')
      if (store.state.donor.swiftaidGivingPlan === 'paid') break
      await new Promise((resolve) => setTimeout(resolve, PLAN_POLL_INTERVAL_MS))
    }
    await refreshDataAfterUpgrade()
    // Same confirmation as the in-modal flow, so a redirect return feels identical.
    showToast(t('Dashboard.Toast.UpgradeComplete'))
  })
}
